import React, { useState, useEffect } from 'react';
import styled from '@emotion/styled/macro';
import EmbedElement from '../../components/embed/embed-element';
import IframeElement from '../../components/iframe/iframe-element';
import vars from '../../variables';
import { EmbedBlockProps } from './embed-block.types';

const EmbedBlock: React.FC<EmbedBlockProps> = ({
  embedId,
  titleCmsOnly,
  title,
  displayTitle,
  anchorId,
  description,
  url,
  isFullWidth,
  mobileHeight,
  largeMobileHeight,
  smallTabletHeight,
  tabletHeight,
  largeTabletHeight,
  smallDesktopHeight,
  desktopHeight,
  className,
}) => {
  const [isOverlayScrollbar, setIsOverlayScrollbar] = useState(false);

  useEffect(() => {
    const detect = () => {
      const root = document.documentElement;
      const hasVScroll = root.scrollHeight > root.clientHeight;
      const gutter = window.innerWidth - root.clientWidth; // 0 when overlaid
      setIsOverlayScrollbar(hasVScroll && gutter === 0);
    };
    detect();
    window.addEventListener('resize', detect);
    return () => window.removeEventListener('resize', detect);
  }, []);

  if (url && url.indexOf('.pdf') !== -1) {
    //pdf
    return (
      <EmbedContainer
        data-testid="embed-block"
        isFullWidth={isFullWidth}
        isOverlayScrollbar={isOverlayScrollbar}
        mobileHeight={mobileHeight}
        largeMobileHeight={largeMobileHeight}
        smallTabletHeight={smallTabletHeight}
        tabletHeight={tabletHeight}
        largeTabletHeight={largeTabletHeight}
        smallDesktopHeight={smallDesktopHeight}
        desktopHeight={desktopHeight}
      >
        <EmbedElement
          id={anchorId}
          embedId={embedId}
          className={className}
          isFullWidth={isFullWidth}
          title={title}
          displayTitle={displayTitle}
          description={description}
          url={url}
        />
      </EmbedContainer>
    );
  } else if (url) {
    //iframe
    return (
      <EmbedContainer
        data-testid="embed-block"
        isFullWidth={isFullWidth}
        isOverlayScrollbar={isOverlayScrollbar}
        mobileHeight={mobileHeight}
        largeMobileHeight={largeMobileHeight}
        smallTabletHeight={smallTabletHeight}
        tabletHeight={tabletHeight}
        largeTabletHeight={largeTabletHeight}
        smallDesktopHeight={smallDesktopHeight}
        desktopHeight={desktopHeight}
      >
        <IframeElement
          className={className}
          isFullWidth={isFullWidth}
          title={title}
          description={description}
          url={url}
        />
      </EmbedContainer>
    );
  } else {
    console.warn(`Embed block called with missing url: ${titleCmsOnly}`);
    return null;
  }
};

const EmbedContainer = styled.div<{
  isFullWidth?: boolean;
  scrollbarWidth?: number;
  mobileHeight?: string;
  isOverlayScrollbar?: boolean;
  largeMobileHeight?: string;
  smallTabletHeight?: string;
  tabletHeight?: string;
  largeTabletHeight?: string;
  smallDesktopHeight?: string;
  desktopHeight?: string;
  scrollbarCompensation?: string;
}>`
  margin-top: ${vars.spacingXl};
  margin-bottom: ${vars.spacingXl};

  ${({ isFullWidth, isOverlayScrollbar }) =>
    isFullWidth &&
    (isOverlayScrollbar
      ? `
        width: 100vw;
        position: relative;
        left: calc(-50vw + 50%);
        margin-top: 3.2rem;
        margin-bottom: 3.2rem;
      `
      : `
        width: calc(100vw - 15px);
        position: relative;
        left: calc(-50vw + 50% + 7.5px); /* precise correction */
        margin-top: 3.2rem;
        margin-bottom: 3.2rem;
      `)}

  ${EmbedElement} .pdf-embed, ${IframeElement} > iframe {
    height: ${vars.iFrameDefaultHeight};

    @media screen and (max-width: calc(${vars.breakPointTenColumn} - 1px)) {
      ${({ mobileHeight }) => mobileHeight && `height: ${mobileHeight};`}
    }
    @media screen and (min-width: ${vars.breakPointTenColumn}) and (max-width: calc(${vars.breakPointSmallTablet} - 1px)) {
      ${({ largeMobileHeight, mobileHeight }) =>
        largeMobileHeight
          ? `height: ${largeMobileHeight};`
          : mobileHeight && `height: ${mobileHeight};`}
    }
    @media screen and (min-width: ${vars.breakPointSmallTablet}) and (max-width: calc(${vars.breakPointTablet} - 1px)) {
      ${({ smallTabletHeight, tabletHeight }) =>
        smallTabletHeight
          ? `height: ${smallTabletHeight};`
          : tabletHeight && `height: ${tabletHeight};`}
    }
    @media screen and (min-width: ${vars.breakPointTablet}) and (max-width: calc(${vars.breakPointSmallDesktop} - 1px)) {
      ${({ tabletHeight }) => tabletHeight && `height: ${tabletHeight};`}
    }
    @media screen and (min-width: ${vars.breakPointSmallDesktop}) and (max-width: calc(${vars.breakPointTwelveColumn} - 1px)) {
      ${({ largeTabletHeight, tabletHeight }) =>
        largeTabletHeight
          ? `height: ${largeTabletHeight};`
          : tabletHeight && `height: ${tabletHeight};`}
    }
    @media screen and (min-width: ${vars.breakPointTwelveColumn}) and (max-width: calc(${vars.breakPointFullWidth} - 1px)) {
      ${({ smallDesktopHeight, desktopHeight }) =>
        smallDesktopHeight
          ? `height: ${smallDesktopHeight};`
          : desktopHeight && `height: ${desktopHeight};`}
    }
    @media screen and (min-width: ${vars.breakPointFullWidth}) {
      ${({ desktopHeight }) => desktopHeight && `height: ${desktopHeight};`}
    }
  }
`;

export default EmbedBlock;
